import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./styles.css";

export const ReferralArrowIcon = () => (
  <svg aria-hidden="true" width="18" height="12" viewBox="0 0 18 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 6h16M12 1l5 5-5 5" />
  </svg>
);

const ReferralButton = ({ text, onClick }: { text: string; onClick: () => void }) => (
  <button type="button" className="referral-btn" onClick={onClick}>
    {text}
    <ReferralArrowIcon />
  </button>
);

const ModalShell: React.FC<{
  onClose: () => void;
  cardClassName?: string;
  children: React.ReactNode;
}> = ({ onClose, cardClassName = "", children }) => {
  const modalRoot = document.getElementById("modal-root") || document.body;
  return createPortal(
    <div className="ModalOverlay alert-modal-overlay" onClick={onClose}>
      <div
        className={`referral-modal-card ${cardClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="referral-modal-close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
        {children}
      </div>
    </div>,
    modalRoot
  );
};

const REFERRAL_CODE_LENGTH = 6;

// "Did a friend invite you?" — submits as soon as all 6 digits are entered
const ReferralCodeEntry = ({ onApplyCode }: { onApplyCode: (code: string) => Promise<string | null> }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onChange = async (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, REFERRAL_CODE_LENGTH);
    setCode(digits);
    setError(null);
    if (digits.length !== REFERRAL_CODE_LENGTH) return;

    setSubmitting(true);
    const message = await onApplyCode(digits);
    setSubmitting(false);
    if (message) setError(message);
  };

  return (
    <div className="referral-code-entry">
      <p className="referral-code-title">Did a friend invite you?</p>
      <p className="referral-modal-message referral-code-text">
        Enter her {REFERRAL_CODE_LENGTH}-digit code and get your bonus pts!
      </p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={REFERRAL_CODE_LENGTH}
        placeholder={"*".repeat(REFERRAL_CODE_LENGTH)}
        aria-label="Friend's referral code"
        className={`referral-code-input ${error ? "referral-code-input-error" : ""}`}
        value={code}
        disabled={submitting}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <p className="referral-code-error">{error}</p>}
    </div>
  );
};

type ReferralInviteModalProps = {
  visible: boolean;
  points?: number;
  onClose: () => void;
  onInvite: () => void;
  // Shown only to users who have not used a code yet
  onApplyCode?: (code: string) => Promise<string | null>;
};

// "U4U is fun with friends!" — 48h after first login and from the profile "?" button
export const ReferralInviteModal: React.FC<ReferralInviteModalProps> = ({
  visible,
  points = 100,
  onClose,
  onInvite,
  onApplyCode,
}) => {
  if (!visible) return null;
  return (
    <ModalShell onClose={onClose} cardClassName="referral-modal-gradient">
      <img
        src={require("../assets/referral_friends.png")}
        alt="U4U is fun with friends!"
        className="referral-friends-image"
      />
      <p className="referral-modal-message">
        When your friend joins U4U + does her first challenge or check-in, you both get{" "}
        <span className="font-bold">{points} bonus pts!</span>
      </p>
      <ReferralButton text="INVITE A FRIEND" onClick={onInvite} />
      {onApplyCode && <ReferralCodeEntry onApplyCode={onApplyCode} />}
    </ModalShell>
  );
};

type ReferralNotificationModalProps = {
  visible: boolean;
  type: "joined" | "rewarded";
  username: string;
  points?: number;
  onClose: () => void;
  onInvite: () => void;
};

// Shown to the referrer when a friend joins / completes her first activity
export const ReferralNotificationModal: React.FC<ReferralNotificationModalProps> = ({
  visible,
  type,
  username,
  points = 100,
  onClose,
  onInvite,
}) => {
  if (!visible) return null;
  return (
    <ModalShell onClose={onClose}>
      {type === "joined" ? (
        <>
          <h2 className="alert-modal-title invite-title">Invite Accepted! 🎉</h2>
          <p className="referral-modal-message mb-0 invite-text">Your friend</p>
          <p className="referral-modal-username invite-username">{username}</p>
          <p className="referral-modal-message mt-0 invite-text">joined U4U!</p>
        </>
      ) : (
        <>
          <div className="justify-center flex">
            <img src={require("../assets/woohoo.png")} alt="Woohoo!" className="alert-modal-title-image" />
          </div>
          <p className="referral-modal-message invite-text">
            You got <span className="font-bold">{points} bonus points!</span>
          </p>
          <img
            src={require("../assets/referral_high_five.png")}
            alt=""
            className="referral-high-five-image"
          />
          <p className="referral-modal-username invite-username">{username}</p>
          <p className="referral-modal-message mt-0 invite-text">did her first challenge or check-in.</p>
        </>
      )}
      <ReferralButton text="INVITE ANOTHER FRIEND" onClick={onInvite} />
    </ModalShell>
  );
};
